import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import {
  Clock,
  ArrowUp,
  Ruler,
  Footprints,
  Gauge,
  Flame
} from "lucide-react";
import RouteMapComponent from "@/app/routes/components/RouteMapComponent";

// Define the RouteData interface based on the iOS app's RoutePostData
export interface RouteData {
  type: string;
  date: Date;
  duration: number; // in seconds
  distance: number; // in km
  averageSpeed: number; // in km/h
  elevationGain: number; // in meters
  coordinates: Array<{latitude: number, longitude: number}>;
  steps: number;
  calories: number;
  location?: string;
}

interface RoutePostContentProps {
  routeData: RouteData;
}

// Format duration from seconds to hh:mm:ss or mm:ss
const formatDuration = (duration: number): string => {
  const hours = Math.floor(duration / 3600);
  const minutes = Math.floor((duration % 3600) / 60);
  const seconds = Math.floor(duration % 60);
  
  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  } else if (minutes > 0) {
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  } else {
    return `${seconds}s`;
  }
};

// Format pace (min/km) from speed (km/h)
const formatPace = (speed: number): string => {
  if (speed <= 0) return "0:00";
  
  // Convert km/h to min/km
  const minutesPerKm = 60 / speed;
  const minutes = Math.floor(minutesPerKm);
  const seconds = Math.floor((minutesPerKm - minutes) * 60);
  
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

// Format date for header
const formatDateHeader = (date: Date): string => {
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  }).toUpperCase();
};

// Format time for header
const formatTimeHeader = (date: Date): string => {
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit'
  }).toUpperCase();
};

export const RoutePostContent: React.FC<RoutePostContentProps> = ({ routeData }) => {
  console.log("RoutePostContent received routeData:", routeData);
  
  // Check if routeData is undefined or null
  if (!routeData) {
    console.error("RoutePostContent received undefined or null routeData");
    return <div>No route data available</div>;
  }
  
  // Ensure date is a Date object
  const date = routeData.date instanceof Date ? routeData.date : new Date(routeData.date);
  
  // Ensure all required fields have default values
  const safeRouteData = {
    type: routeData.type || "running",
    date: date,
    duration: routeData.duration || 0,
    distance: routeData.distance || 0,
    averageSpeed: routeData.averageSpeed || 0,
    elevationGain: routeData.elevationGain || 0,
    coordinates: routeData.coordinates || [],
    steps: routeData.steps || 0,
    calories: routeData.calories || 0,
    location: routeData.location || null
  };
  
  return (
    <Card className="w-full bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-800 overflow-hidden mt-2">
      <CardContent className="p-0">
        {/* Header with date and time */}
        <div className="flex justify-between items-center p-4 bg-blue-50 dark:bg-blue-950">
          <div className="text-blue-600 dark:text-blue-400 text-xl font-bold">
            {formatDateHeader(date)}
          </div>
          <div className="text-blue-600 dark:text-blue-400 text-xl font-bold">
            {formatTimeHeader(date)}
          </div>
        </div>
        
        {/* Exercise type */}
        <div className="bg-blue-100 dark:bg-blue-900 py-2 px-4">
          <div className="inline-block bg-blue-500 text-white px-4 py-1 rounded-full font-medium">
            {safeRouteData?.type?.charAt(0).toUpperCase() + safeRouteData?.type?.slice(1)}
          </div>
        </div>
        
        {/* Metrics grid */}
        <div className="grid grid-cols-2 gap-y-4 p-4">
          {/* Duration */}
          <div className="flex items-center gap-3">
            <Clock className="h-6 w-6 text-blue-500" />
            <div>
              <p className="text-slate-500 dark:text-slate-400 text-sm">Duration</p>
              <p className="font-semibold">{formatDuration(safeRouteData.duration)}</p>
            </div>
          </div>
          
          {/* Elevation */}
          <div className="flex items-center gap-3">
            <ArrowUp className="h-6 w-6 text-blue-500" />
            <div>
              <p className="text-slate-500 dark:text-slate-400 text-sm">Elevation</p>
              <p className="font-semibold">{safeRouteData.elevationGain} m</p>
            </div>
          </div>
          
          {/* Distance */}
          <div className="flex items-center gap-3">
            <Ruler className="h-6 w-6 text-blue-500" />
            <div>
              <p className="text-slate-500 dark:text-slate-400 text-sm">Distance</p>
              <p className="font-semibold">{safeRouteData?.distance?.toFixed(1)} km</p>
            </div>
          </div>
          
          {/* Steps */}
          <div className="flex items-center gap-3">
            <Footprints className="h-6 w-6 text-blue-500" />
            <div>
              <p className="text-slate-500 dark:text-slate-400 text-sm">Steps</p>
              <p className="font-semibold">{safeRouteData.steps}</p>
            </div>
          </div>
          
          {/* Average Speed/Pace */}
          <div className="flex items-center gap-3">
            <Gauge className="h-6 w-6 text-blue-500" />
            <div>
              <p className="text-slate-500 dark:text-slate-400 text-sm">
                {safeRouteData?.type?.toLowerCase() === "cycling" ? "Average Speed" : "Average Pace"}
              </p>
              <p className="font-semibold">
                {safeRouteData?.type?.toLowerCase() === "cycling"
                  ? `${safeRouteData?.averageSpeed?.toFixed(1)} km/h`
                  : `${formatPace(safeRouteData.averageSpeed)} min/km`}
              </p>
            </div>
          </div>
          
          {/* Calories */}
          <div className="flex items-center gap-3">
            <Flame className="h-6 w-6 text-blue-500" />
            <div>
              <p className="text-slate-500 dark:text-slate-400 text-sm">Calories</p>
              <p className="font-semibold">{safeRouteData.calories} kcal</p>
            </div>
          </div>
        </div>
        
        {/* Map */}
        {safeRouteData?.coordinates?.length > 0 && (
          <div className="w-full h-64">
            <RouteMapComponent
              coordinates={safeRouteData.coordinates}
              startPoint={safeRouteData.coordinates[0]}
              endPoint={safeRouteData.coordinates[safeRouteData?.coordinates?.length - 1]}
              className="h-full w-full"
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default RoutePostContent;